export const fontFamily = 'DMSans'
export const gapAubergine = '#331435'
export const gapPassion = '#EF6737'

export type DialogSize = 'extra small' | 'small' | 'medium' | 'large' | 'extra large'
const dialogSizes: Record<DialogSize, number> = {
   'extra small': 444,
   small: 600,
   medium: 900,
   large: 1200,
   'extra large': 1536,
}

/** Call at startup before React renders to invert the primary palette for dark mode */
export function initThemeColors(isDark: boolean) {
   if (!isDark) return
   const p = themeConfig.primary
   const swap = (a: keyof typeof p, b: keyof typeof p) => {
      const t = p[a]
      p[a] = p[b]
      p[b] = t
   }
   swap('50', '900')
   swap('100', '800')
   swap('200', '700')
   swap('300', '600')
   swap('400', '500')
}

export const themeConfig = {
   fontFamily,
   spacing: 8,
   popperShadow: '0 2px 12px 0 rgba(0, 0, 0, 0.5)',
   snackbar: {
      success: '#2E7D32',
      info: gapAubergine,
      warning: '#ED6C02',
      error: '#D32F2F',
   },
   linkButtonHoverColor: '#eeeeee',
   // Gap passion colour (orange)
   passion: gapPassion,
   primary: {
      '50': '#FAF7FA',
      '100': '#F4F0F5',
      '200': '#E2DAE3',
      '300': '#CBBECC',
      '400': '#A797A8',
      '500': '#867387',
      '600': '#655266',
      '700': '#4B2E4D',
      '800': gapAubergine,
      '900': '#18001A',
   },
   grey: {
      '50': '#FAFAFA',
      '100': '#F5F5F5',
      '200': '#EEEEEE',
      '300': '#E0E0E0',
      '400': '#bdbdbd',
      '500': '#9E9E9E',
      '600': '#757575',
      '700': '#616161',
      '800': '#424242',
      '900': '#212121',
   },
   dark: {
      action: {
         hover: 'rgba(255, 255, 255, 0.08)',
      },
   },
   light: {
      action: {
         disabled: {
            '300': '#CBBECC',
         },
         active: '#331435',
         selected: '#E2DAE3', // primary[200]
         hover: '#CBBECC', // primary[300]
      },
      background: {
         alt: '#F7F6F2',
      },
      borderColour: '#A797A8',
      borderColourHover: gapAubergine,
   },
   system: {
      info: {
         '50': '#F3F8FD',
         '100': '#D7E8FC',
         '200': '#AFD1F9',
         '300': '#87BAF5',
         '400': '#5FA3F2',
         '500': '#378CEF',
         '600': '#2C70BF',
         '700': '#21548F',
         '800': '#163860',
         '900': '#0B1C30',
      },
      success: {
         '50': '#F7FAF9',
         '100': '#E6F5EE',
         '200': '#D5EDE3',
         '300': '#B5E1CE',
         '400': '#87C7AB',
         '500': '#55A381',
         '600': '#388061',
         '700': '#28664B',
         '800': '#1C4D38',
         '900': '#0E3323',
      },
      warning: {
         '50': '#FDFAF5',
         '100': '#FCEDD7',
         '200': '#F9DBAF',
         '300': '#F5C987',
         '400': '#F2B75F',
         '500': '#E59E35',
         '600': '#B77E2A',
         '700': '#895F20',
         '800': '#604216',
         '900': '#30210B',
      },
      error: {
         '50': '#FDF5F3',
         '100': '#FCDED7',
         '200': '#F9BEAF',
         '300': '#F59D87',
         '400': '#F27D5F',
         '500': '#EF5C37',
         '600': '#BF4A2C',
         '700': '#8F3721',
         '800': '#602516',
         '900': '#30120B',
      },
   },
   accents: {
      pink: {
         '50': '#FBF0FB',
         '100': '#F3D3F2',
         '200': '#EBB5E9',
         '300': '#E297E0',
         '400': '#DA7AD7',
         '500': '#D66BD2',
         '600': '#C160BD',
         '700': '#964B93',
         '800': '#6B3669',
         '900': '#40203F',
      },
      orange: {
         // orange(passion)
         '50': '#FDF0EB',
         '100': '#FCE1D7',
         '200': '#F9C2AF',
         '300': '#F5A487',
         '400': '#F2855F',
         '500': '#EF6737',
         '600': '#BF522C',
         '700': '#8F3E21',
         '800': '#602916',
         '900': '#30150B',
      },
      violet: {
         '50': '#F5F3FB',
         '100': '#E1DCF3',
         '200': '#CDC5EB',
         '300': '#ADA3D6',
         '400': '#A497DA',
         '500': '#9A8BD6',
         '600': '#8B7DC1',
         '700': '#6C6196',
         '800': '#4D466B',
         '900': '#2E2A40',
      },
      teal: {
         '50': '#F1F9FA',
         '100': '#C0D7DC',
         '200': '#96BCC5',
         '300': '#6BA1AD',
         '400': '#418696',
         '500': '#2C788A',
         '600': '#286C7C',
         '700': '#1F5461',
         '800': '#163B44',
         '900': '#0D2429',
      },
      lightblue: {
         '50': '#EEF9FB',
         '100': '#CCECF3',
         '200': '#ABDFEB',
         '300': '#89D2E2',
         '400': '#67C5DA',
         '500': '#56BFD6',
         '600': '#4DACC1',
         '700': '#3C8696',
         '800': '#2B606B',
         '900': '#1A3940',
      },
      yellow: {
         // Yellow (off-white)
         '50': '#F7F6F2',
         '100': '#F6F2E8',
         '200': '#FCF2D7',
         '300': '#F9E0A3',
         '400': '#F7D788',
         '500': '#F6D37B',
         '600': '#DDBE6F',
         '700': '#AC9456',
         '800': '#7B6A3E',
         '900': '#4A3F25',
      },
   },
   destroyButtonColor: '#E53935',
   infoBox: {
      textColor: gapAubergine,
      borderColor: '#F2E9F2',
   },
   warningBox: {
      textColor: '#30210B', // themeConfig.system.warning[900]
      borderColor: '#F9DBAF', // themeConfig.system.warning[200]
   },
   dialog: {
      // Default is 1300, but now we have it defined
      zIndex: 1300,
   },
   dialogAboveDrawer: {
      // For dialogs that need to appear above drawers (drawer is 1300)
      zIndex: 1400,
   },
   autoCompletePopper: {
      zIndex: 1500,
   },
   background: {
      /** Usage: backgroundImage: themeConfig.background.sixtyThirty */
      sixtyThirty: `linear-gradient(to top, transparent 0%, transparent 33%, ${gapAubergine} 33%, ${gapAubergine} 100%)`,
   },
   breakpoints: {
      values: {
         // Default breakpoints from mui
         xs: 0,
         sm: 600,
         md: 900,
         lg: 1200,
         xl: 1536,
         // Breakpoints defined by us
         mobile: 0,
         tablet: 600,
         desktop: 900,
      },
   },
   typographyProps: {
      fontFamily: [fontFamily].join(','),
      fontSize: 16,
      h1: {
         fontSize: '4.5rem',
         fontWeight: 'bold',
         letterSpacing: '-1.5px',
      },
      h2: {
         fontSize: '3.5rem',
         fontWeight: 'bold',
         letterSpacing: '-0.5px',
      },
      h3: {
         fontSize: '2.75rem',
         fontWeight: 'bold',
         letterSpacing: '0px',
      },
      h4: {
         fontSize: '2.25rem',
         fontWeight: 'bold',
         letterSpacing: '0.25px',
      },
      h5: {
         fontSize: '1.875rem',
         fontWeight: 'bold',
         letterSpacing: '0px',
      },
      h6: {
         fontSize: '1.5rem',
         fontWeight: 'bold',
         letterSpacing: '0.15px',
      },
      subtitle1: {
         fontSize: '1.25rem',
         fontWeight: 'normal',
         letterSpacing: '0.15px',
      },
      subtitle1Bold: {
         fontSize: '1.25rem',
         fontWeight: 'bold',
         letterSpacing: '0.15px',
      },
      subtitle2: {
         fontSize: '1.125rem',
         fontWeight: 'normal',
         letterSpacing: '0.1px',
      },
      subtitle2Bold: {
         fontSize: '1.125rem',
         fontWeight: 'bold',
         letterSpacing: '0.1px',
      },
      body1: {
         fontSize: '1rem',
         fontWeight: 'normal',
         letterSpacing: '0.15px',
      },
      body1Bold: {
         fontSize: '1rem',
         fontWeight: 'bold',
         letterSpacing: '0.15px',
      },
      body2: {
         fontSize: '0.875rem',
         letterSpacing: '0.15px',
      },
      body2Bold: {
         fontSize: '0.875rem',
         fontWeight: 'bold',
         letterSpacing: '0.15px',
      },
      caption: {
         fontSize: '0.75rem',
         letterSpacing: '0.4px',
      },
      overline: {
         fontSize: '0.75rem',
         letterSpacing: '1px',
         textTransform: 'uppercase',
      } as any,
   },
   dialogSizes,
}
